import type { InfoSheetContent } from '@uiKit/components/InfoSheet.vue'

// ⓘ copy behind the calculator's Total cost lines and its Acquisition Cost
// sheet — the app's verbatim (investor-app offerings/property/constants/
// infoContent.ts). Each line is matched to its copy in propertyCalculatorUtils.

export interface FeeInfo {
  sheet: InfoSheetContent
  /** "How it's calculated:" — only on sheets that work a formula. */
  heading?: string
  formula?: string
}

export const CALCULATOR_FEE_INFO = {
  // ── Total cost lines ───────────────────────────────────────────────────
  dldFee: {
    sheet: {
      title: 'DLD Fee',
      note: 'The Dubai Land Department’s transfer fee',
      description:
        'The government fee charged on every property transfer in Dubai, calculated on your share of the purchase price. It is collected on your behalf and paid straight to the Land Department — Sliceal keeps none of it.',
    },
  },
  titleDeedFee: {
    sheet: {
      title: 'Title Deed Fee',
      note: "DLD's fee for issuing the property's title deed",
      description:
        'The fee is shared across all investors in proportion to how much of the property each person owns. No VAT applies.',
    },
  },
  nocFee: {
    sheet: {
      title: 'NOC & Documentation Fee',
      note: 'Fees for the property transfer documents from the Dubai Land Department',
      description:
        "Covers the developer's No Objection Certificate, which is needed before the property can be transferred, along with the transfer paperwork. It's shared across all investors in proportion to ownership. No VAT applies.",
    },
  },
  platformFee: {
    sheet: {
      title: 'Platform Fee (2%)',
      note: "Sliceal's one-time fee for your investment",
      description:
        "Sliceal's fee for sourcing, structuring and managing your investment. It's 2% of your investment amount, with a minimum of AED 40, and it's charged once, when you buy. VAT at 5% applies.",
    },
  },
  platformFeeVat: {
    sheet: {
      title: 'VAT on Platform Fee (5%)',
      note: 'UAE VAT, charged on the Platform Fee only',
      description:
        "There's no VAT on your investment amount or on government charges. You'll find it itemised on your tax invoice.",
    },
  },
  acquisitionCost: {
    sheet: {
      title: 'Acquisition Cost',
      note: 'The one-time costs of buying and preparing this property',
      description:
        'These cover everything needed to acquire the property, get it ready for a tenant and create its tokens. They are shared across all investors in proportion to ownership, and are charged once at the time of purchase.',
    },
  },
  acquisitionFee: {
    sheet: {
      title: 'Acquisition Fee',
      note: 'A one-time fee charged by Sliceal for facilitating your investment, calculated as a percentage of the amount you invest',
      description:
        "It is applied at the point of purchase and covers the platform's costs of processing, tokenising, and administering your holding.",
    },
    heading: "How it's calculated:",
    formula: 'Acquisition Fee (AED) = Investment Amount × 1%',
  },

  // ── Acquisition Cost sheet rows ────────────────────────────────────────
  brokerageFee: {
    sheet: {
      title: 'Brokerage Fee',
      note: 'The commission for finding and securing this property',
      description:
        'Paid to the agent who sourced the property and negotiated the purchase on behalf of investors. A one-time cost at purchase, including VAT.',
    },
  },
  trusteeFee: {
    sheet: {
      title: 'Real Estate Trustee Fee',
      note: 'The charge for processing the ownership transfer',
      description:
        'Paid to the DLD-licensed trustee office that carries out the transfer of ownership and registers it with the Dubai Land Department. A one-time cost at purchase, including VAT.',
    },
  },
  valuationFee: {
    sheet: {
      title: 'Valuation & Condition Survey Fee',
      note: 'The cost of independently valuing and inspecting the property',
      description:
        'An accredited valuer confirms what the property is worth and checks its condition before purchase, so you know the asset has been assessed independently. A one-time cost at purchase, including VAT.',
    },
  },
  conveyancingFee: {
    sheet: {
      title: 'Conveyancing Fee',
      note: 'The legal cost of transferring ownership',
      description:
        'Paid to a conveyancer where one is engaged to handle the legal side of the transfer. This is not required for every property and shows as zero when it does not apply.',
    },
  },
  utilitiesConnectionFee: {
    sheet: {
      title: 'Utilities Connection Fee',
      note: 'The cost of connecting water and electricity',
      description:
        'Covers registering the property with DEWA and activating its utilities so it is ready for a tenant. Refundable security deposits are not included. A one-time cost at purchase, including VAT.',
    },
  },
  furnishingBudget: {
    sheet: {
      title: 'Furnishing Budget',
      note: 'The cost of furnishing the property for tenants',
      description:
        'Covers furniture and fittings where the property is let furnished. It applies only to properties that need it and shows as zero otherwise.',
    },
  },
  renovationCost: {
    sheet: {
      title: 'Renovation / Refurbishment',
      note: 'The cost of any work needed before the property is let',
      description:
        'Covers repairs or upgrades carried out before a tenant moves in. It applies only to properties that need work and shows as zero otherwise.',
    },
  },
  maintenanceReserve: {
    sheet: {
      title: 'Maintenance Reserve',
      note: 'Money set aside for unexpected repairs',
      description:
        'Held for the property so that a sudden repair does not interrupt your rental income. Any unused balance is returned to token holders when the property is sold. No VAT applies.',
    },
  },
  propertyInsuranceCost: {
    sheet: {
      title: 'Property Insurance Cost (One-Time)',
      note: "The property's first insurance premium",
      description:
        'Covers the property against damage and loss from the day it is acquired. Later renewals are paid from rental income rather than charged here. A one-time cost at purchase, including VAT.',
    },
  },
  tokenisationFee: {
    sheet: {
      title: 'Tokenisation Fee (0.01%)',
      note: "The cost of creating this property's tokens",
      description:
        'Paid to our regulated tokenisation partner for issuing the tokens and recording your ownership on the blockchain. A one-time cost at purchase, including VAT.',
    },
  },
} satisfies Record<string, FeeInfo>

export type CalculatorFeeInfoKey = keyof typeof CALCULATOR_FEE_INFO
